'use client';

import {
  ImageLightbox,
  useImageViewer,
  type ViewerImage,
} from '@kit/ui/image-viewer';

export interface OrganizationGalleryImage {
  id: string;
  src: string;
  alt: string;
}

interface OrganizationGalleryProps {
  label: string;
  images: OrganizationGalleryImage[];
}

/**
 * Client gallery grid for the public organization page (BAL-380).
 *
 * Clicking any tile opens the photo in the canonical `@kit/ui/image-viewer`
 * lightbox (mirrors the dancer-profile gallery, BAL-370). Tiles are buttons so
 * the lightbox is keyboard-reachable; the viewer indexes over the photos in
 * render order so prev/next walks the grid left-to-right, top-to-bottom.
 */
export function OrganizationGallery({
  label,
  images,
}: OrganizationGalleryProps) {
  const viewerImages: ViewerImage[] = images.map((img) => ({
    id: img.id,
    src: img.src,
    alt: img.alt,
  }));

  const viewer = useImageViewer(viewerImages);

  if (images.length === 0) return null;

  return (
    <section aria-label={label}>
      <p className="text-muted-foreground mb-5 text-[11px] font-semibold tracking-widest uppercase">
        {label}
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((img, index) => (
          // eslint-disable-next-line kit-components/use-kit-primitives -- aspect-square photo tile; @kit/ui Button's default size forces h-9, collapsing the square image tile to a 36px strip — full-bleed image wrapper, not button chrome.
          <button
            key={img.id}
            type="button"
            onClick={() => viewer.openAt(index)}
            className="bg-muted focus-visible:ring-ring relative aspect-square cursor-zoom-in overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- /api/media/ returns a 302 redirect */}
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform hover:scale-105"
            />
          </button>
        ))}
      </div>

      <ImageLightbox
        images={viewerImages}
        index={viewer.index}
        onIndexChange={viewer.goTo}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        showDownload={false}
      />
    </section>
  );
}
